import { autoinject, transient } from "aurelia-framework";
import { GetVesselsResponse, VesselModel } from "../../../../models/myvessels.models";
import { MyVesselService } from "../../../../services/my-vessel.service";
import { Router } from "aurelia-router";
import { myVesselsDialogConstants } from "../../../../constants/dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import { MyVesselsRouteConstants } from "../../../../constants/route.constants";
import { EmailRecipientTypeEnum, SelectVesselTypeEnum } from "../../../../models/common.models";
import { ItemSource } from "@rightship-tech/common-ui/models/queries.models";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { FormTypesEnum, FormsStatusEnum } from "../../../../models/form.model";
import { VesselService } from "../../../../services/vessel.service";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { FormService } from "../../../../services/form.service";

@autoinject
@transient()
export class SelectVessel {

    public type: SelectVesselTypeEnum.MissingPsc | SelectVesselTypeEnum.ClassStatusReport | SelectVesselTypeEnum.ChangeOfManagement;
    public title: string;
    public subTitle: string;
    public vessels: GetVesselsResponse;
    public canAddVessel: boolean = false;
    public itemSource: ItemSource;

    constructor(private router: Router, private myVesselService: MyVesselService,
        private userClaimsService: UserClaimsService, private dialogClientService: DialogClientService,
        private configService: ConfigService,
        private vesselService: VesselService, private userClientSerivce: UserClientService, private formService: FormService) {
            this.canAddVessel = this.userClaimsService.hasPermission(Claims.CanAddVesselRightFLEETStarter);
    }

    public activate(params: { title: string, subTitle: string, 
        type: SelectVesselTypeEnum.MissingPsc | SelectVesselTypeEnum.ClassStatusReport | SelectVesselTypeEnum.ChangeOfManagement }) {

        this.title = params.title;
        this.subTitle = params.subTitle;
        this.type = params.type;
    }

    public async attached() {

        //Get vessels
        await this.loadVessels();
    }

    //Check if the user can navigate back
    public canNavigateBack():boolean {
		return this.router.history && this.router.history.getHistoryIndex() > 0;
	}

    //Navigate back
    public navigateBack() {
		this.router.navigateBack();
	}

    //Choose vessel based on the type passed
    async chooseVessel(vessel: VesselModel) {

        //If there is a vessel loading, do nothing
        if(this.vessels.result.filter(x => x.isLoading).length > 0) return

        //Else set this vessel as loading true
        vessel.isLoading = true;

        try {

            switch (this.type) {
                case SelectVesselTypeEnum.MissingPsc: {

                    //Navigate to add psc page
                    this.router.navigateToRoute(MyVesselsRouteConstants.AddPsc, {id: 0, vesselId: vessel.vesselId, identifier: vessel.identifier, vesselName: vessel.vesselName});
                    return;
                }
                case SelectVesselTypeEnum.ClassStatusReport: {

                    //Get class status reports
                    let title = "A Class Status Report is Currently Under Review";
                    let description = "Please wait for the class status report to be completed for <b>" + vessel.vesselName + ", IMO " + vessel.internationalMaritimeOrganizationNumber + "</b>";
                    let formName = "Class Status Report";

                    await this.createOrOpenVesselForm(vessel, FormTypesEnum.ClassStatusReport, formName, title, description);
                    return;
                }
                case SelectVesselTypeEnum.ChangeOfManagement: {

                    //Get Moc forms
                    let title = "Management of Change (MoC) / Vessel Take Over Process (VTOP) Questionnaire Currently Under Review";
                    let description = "Please wait for the Management of Change (MoC) / Vessel Take Over Process (VTOP) Questionnaire to be completed for <b>" + vessel.vesselName + ", IMO " + vessel.internationalMaritimeOrganizationNumber + "</b>";
                    let formName = "Management of Change (MoC) / Vessel Take Over Process (VTOP) Questionnaire";

                    await this.createOrOpenVesselForm(vessel, FormTypesEnum.ChangeOfManagement, formName, title, description);
                    return;
                }
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {

            //Set vessel loading to false
            vessel.isLoading = false;
        }
    }

    async createOrOpenVesselForm(vessel: VesselModel, formType: FormTypesEnum, formName:string, title: string, description: string) {
        // get forms
        let forms = await this.formService.getVesselForms(vessel.vesselId, formType);

        //If theres 1 or more forms in Pending RightShip/terminal Review status, show dialog
        if (forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingReview).length > 0) {

            //Show status info dialog
            this.dialogClientService.show(myVesselsDialogConstants.statusInfoDialog, 
                { 
                    title: title, 
                    description: description
                }, true);    
            return;
        }
        //If theres 1 or more forms in Pending Form Input status and need more info, show dialog
        else if (forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingFormInput && x.isNeedMoreInfo == true).length > 0) {
            let pendingFormInput = forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingFormInput && x.isNeedMoreInfo == true)[0];
            let onboardingRequest = await this.formService.getOnboardingRequest(pendingFormInput.onboardingRequestId);

            //Show need more info dialog
            this.dialogClientService.show(myVesselsDialogConstants.needMoreInfoDialog, {
                entityId: pendingFormInput.vesselApplicationFormId,
                entityTypeId: onboardingRequest.entityTypeId,
                formName: formName,
                vesselName: vessel.vesselName,
                vesselImo: vessel.internationalMaritimeOrganizationNumber,
                onCompleteForm: (async () => {

                    let onbardingConfig = await this.formService.getOnboardingConfig(pendingFormInput.onboardingConfigId);
                    let landingPageGuid = onbardingConfig.onboardingConfig.landingPages[0].guid;
                    let onboardingRequestGuid = onboardingRequest.onboardingRequest.onboardingRequestGuid;
                    window.open(`${this.configService.config.urls.rightShipPlatformUrl}/OnboardingClient/${landingPageGuid}?onboardingRequestGuid=${onboardingRequestGuid}`, "_blank");
                }).bind(this)
            }, true, false, false, "right");
        }
        //if theres 1 or more forms in Pending Form Input status and not need more info, show dialog
        else if (forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingFormInput && x.isNeedMoreInfo != true).length > 0) {                        
            let pendingFormInput = forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingFormInput && x.isNeedMoreInfo != true)[0];
            let onbardingConfig = await this.formService.getOnboardingConfig(pendingFormInput.onboardingConfigId);
            let landingPageGuid = onbardingConfig.onboardingConfig.landingPages[0].guid;
            let onboardingRequestGuid = pendingFormInput.onboardingRequestGuid;
            window.open(`${this.configService.config.urls.rightShipPlatformUrl}/OnboardingClient/${landingPageGuid}?onboardingRequestGuid=${onboardingRequestGuid}`, "_blank");
        }
        //if there are no forms in Pending RightShip/terminal Review or Pending Form Input status, create a new form
        else if (forms.filter(x => x.workFlowStatus == FormsStatusEnum.PendingReview ||
            x.workFlowStatus == FormsStatusEnum.PendingFormInput).length == 0) {

            //Get current user email and call create vessel form API
            let userEmail: string = this.userClientSerivce.currentUser.user.email;
            let createFormUrl = await this.vesselService.createVesselForm({
                FormId: formType, 
                Emails: [{Email: userEmail, EmailRecipientTypeId: EmailRecipientTypeEnum.To}], 
                VesselId: vessel.vesselId
            });
        
            //Open a new browser tab to the new form
            if (createFormUrl) {
                window.open(createFormUrl, "_blank");
            }
        }
    }

    //Open add vessel dialog
    openAddVesselDialog() {
        this.dialogClientService.show(myVesselsDialogConstants.addVesselDialog,  {
            onVesselsAdded: (async () => {
                await this.loadVessels();
            }).bind(this)
        }, true, false, false, "right");
    }

    async loadVessels() {
        this.vessels = await this.myVesselService.getMyVessels();
    }
}